import {
  Avatar,
  Box,
  Container,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  Stack,
  Toolbar,
  toolbarClasses,
  Typography,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import React from 'react';

// Components
import { CreditCoinIcon } from 'src/components/Icons';
// import { BellIcon, ChatBubbleIcon } from 'src/components/Icons';
// Utilities
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { IMenuItem } from 'src/components/Drawer/SidebarMenu';
import { queryClient } from 'src/configs/queryClient';
import useAuth from 'src/hooks/useAuth';
import { ME_KEY } from 'src/services/queries/useQueryAuth';
import { DRAWER_WIDTH, DRAWER_WIDTH_CLOSE, EmptyText, HEADER_HEIGHT } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

import { Users } from 'react-feather';
import IconFeather from 'src/components/common/Icon';
import { CustomMenuIcon } from 'src/components/Drawer/Common';
import { ListItem, ListItemButton } from 'src/components/Drawer/SidebarStyled';
import MenuItemAccountSettingIcon from 'src/components/Icons/MenuItemAccountSetting';
import MenuItemLogOutIcon from 'src/components/Icons/MenuItemLogOut';
import { TypeActivitiesTab } from 'src/features/Activities/config';
import { usePathname } from 'src/hooks/usePathName';
import { UsageInformation } from 'src/model/payment';
import { getNormalizedRoute } from 'src/utils/helpers/utilities';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const StyledContainer = styled(Container)(({ theme }) => ({
  height: HEADER_HEIGHT,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  boxShadow: 'none',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    marginLeft: DRAWER_WIDTH_CLOSE,
    width: `calc(100% - ${DRAWER_WIDTH_CLOSE}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// const DividerIcon = styled(Box)(({ theme }) => ({
//   height: theme.spacing(3),
//   width: 1,
//   backgroundColor: theme.palette.divider,
// }));

const StyledAppbar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderBottom: '1px solid',
  borderBottomColor: theme.palette.divider,
  height: HEADER_HEIGHT,
}));

interface IHeaderProps {
  open: boolean;
  dataUsage: UsageInformation;
}

export default function Header(props: IHeaderProps) {
  const { open, dataUsage } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { logoutFromContext } = useAuth();
  const userInfoFromCache = queryClient.getQueryData([ME_KEY]) as IResponse<IUser>;
  const pathname = usePathname();
  const navigate = useNavigate();

  const routeTitleMap = {
    [AuthRouteKeys.Home]: 'Homepage',
    [AuthRouteKeys.PeopleProfiles]: 'People Search',
    [AuthRouteKeys.CompanyDetail]: 'Company Profile Detail',
    [AuthRouteKeys.CompanyProfiles]: 'Companies Search',
    [AuthRouteKeys.TradeData]: 'Trade Data',
    [AuthRouteKeys.TariffsAndRules]: 'Tariffs And Rules',
    [AuthRouteKeys.Port]: 'All Ports',
    [AuthRouteKeys.AiReport]: 'AI Report',
    [AuthRouteKeys.CompanyTradeData]: 'Company Trade Data',
  };

  const normalizedRoute = getNormalizedRoute(pathname) as keyof typeof routeTitleMap;

  const menuData: IMenuItem[] = [
    {
      text: 'Account Setting',
      icon: <MenuItemAccountSettingIcon sx={{ width: '18px' }} />,
      key: 'account_setting',
      path: AuthRouteKeys.Setting,
      disabled: false,
    },
    {
      text: 'My team',
      icon: <IconFeather muiProps={{ fontSize: 'small' }} icon={<Users />} />,
      key: 'my_team',
      path: AuthRouteKeys.Activities + `?tab=${TypeActivitiesTab.TEAM_MANAGEMENT}`,
      disabled: false,
    },
    {
      text: 'Log Out',
      onClickFn: () => logoutFromContext(),
      icon: <MenuItemLogOutIcon sx={{ width: '18px' }} />,
      key: 'trade_data',
      path: AuthRouteKeys.TradeData,
      disabled: false,
    },
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <StyledAppbar open={open} position="fixed" color="inherit">
      <StyledContainer maxWidth="xl">
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            height: HEADER_HEIGHT,
            ['&.' + toolbarClasses.root]: {
              padding: 0,
            },
          }}
        >
          <Typography color={'text.secondary'}>{routeTitleMap[normalizedRoute]}</Typography>
          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Box>
              <Stack
                sx={{ cursor: 'pointer' }}
                onClick={handleOpenUserMenu}
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
              >
                <IconButton sx={{ p: 0 }}>
                  <Avatar alt={userInfoFromCache.data?.alt} src={userInfoFromCache.data?.avatar_url} sizes="48">
                    {(userInfoFromCache?.data?.first_name || EmptyText).charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Box mx={2}>
                  <Typography variant="subtitle2">
                    {!isEmpty(userInfoFromCache.data?.first_name) || !isEmpty(userInfoFromCache.data?.last_name)
                      ? (userInfoFromCache.data?.first_name || '') + ' ' + (userInfoFromCache.data?.last_name || '')
                      : renderTextOrDefault(userInfoFromCache.data?.username)}
                  </Typography>
                  <Stack spacing={0.5} direction="row" justifyContent="center" alignItems="center">
                    <Typography variant="subtitle2" color="text.secondary">
                      {renderNumberOrDefault(
                        (dataUsage?.data?.usage_total || 0) - (dataUsage?.data?.current_usage || 0),
                      )}{' '}
                      / {renderNumberOrDefault(dataUsage?.data?.usage_total || 0)}
                    </Typography>
                    <CreditCoinIcon fontSize="small" />
                  </Stack>
                </Box>
              </Stack>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-header"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <Stack px={2} py={1}>
                  <Typography fontWeight={'bold'} variant="subtitle2">
                    {!isEmpty(userInfoFromCache.data?.first_name) || !isEmpty(userInfoFromCache.data?.last_name)
                      ? (userInfoFromCache.data?.first_name || '') + ' ' + (userInfoFromCache.data?.last_name || '')
                      : renderTextOrDefault(userInfoFromCache.data?.username)}
                  </Typography>
                  <Typography variant={'subtitle2'} color={'text.secondary'}>
                    {userInfoFromCache.data?.email}
                  </Typography>
                </Stack>
                <Divider />
                <Stack pt={1} pl={1} pr={1} flexDirection={'column'} gap={1}>
                  {menuData.map((menuItem) => {
                    return (
                      <ListItem sx={{ mx: 0 }} key={menuItem.key} disablePadding={true}>
                        <ListItemButton
                          disabled={menuItem.disabled}
                          onClick={() => {
                            if (menuItem.onClickFn) {
                              return menuItem.onClickFn();
                            }
                            if (!menuItem.path) return;
                            return navigate(menuItem.path);
                          }}
                        >
                          <CustomMenuIcon Icon={menuItem.icon} handleOnclick={() => menuItem.onClickFn} />
                          <ListItemText
                            sx={{ pl: 1 }}
                            primary={<Typography variant="subtitle2">{menuItem.text}</Typography>}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Stack>
              </Menu>
            </Box>
          </Stack>
        </Toolbar>
      </StyledContainer>
    </StyledAppbar>
  );
}
